import Phaser from 'phaser';
import { Player } from './Player';

export class ExperienceGem extends Phaser.Physics.Arcade.Sprite {
  public xpValue: number;
  private player: Player;
  private magnetRange: number = 100;
  private magnetSpeed: number = 300;

  constructor(scene: Phaser.Scene, x: number, y: number, player: Player, xpValue: number = 1) {
    super(scene, x, y, '');

    this.xpValue = xpValue;
    this.player = player;

    scene.add.existing(this);
    scene.physics.add.existing(this);

    // Create gem texture
    this.createGemTexture(scene);
    this.setTexture('xp-gem');

    // Physics setup
    this.setCollideWorldBounds(false);

    // Disable bobbing animation for performance
  }

  private createGemTexture(scene: Phaser.Scene): void {
    const graphics = scene.add.graphics();
    const size = 14;
    const halfSize = size / 2;

    // Outer glow
    graphics.fillStyle(0x00FF00, 0.3);
    graphics.fillCircle(halfSize, halfSize, 6);

    // Main gem body (darker green base)
    graphics.fillStyle(0x00CC00, 1);
    graphics.beginPath();
    graphics.moveTo(halfSize, halfSize - 5);
    graphics.lineTo(halfSize + 5, halfSize);
    graphics.lineTo(halfSize, halfSize + 5);
    graphics.lineTo(halfSize - 5, halfSize);
    graphics.closePath();
    graphics.fillPath();

    // Brighter green overlay
    graphics.fillStyle(0x00FF00, 1);
    graphics.beginPath();
    graphics.moveTo(halfSize, halfSize - 5);
    graphics.lineTo(halfSize + 4, halfSize);
    graphics.lineTo(halfSize, halfSize + 4);
    graphics.lineTo(halfSize - 4, halfSize);
    graphics.closePath();
    graphics.fillPath();

    // Facets (darker sections for depth)
    graphics.fillStyle(0x00AA00, 1);
    graphics.beginPath();
    graphics.moveTo(halfSize, halfSize);
    graphics.lineTo(halfSize + 4, halfSize);
    graphics.lineTo(halfSize, halfSize + 4);
    graphics.closePath();
    graphics.fillPath();

    graphics.beginPath();
    graphics.moveTo(halfSize, halfSize);
    graphics.lineTo(halfSize - 4, halfSize);
    graphics.lineTo(halfSize, halfSize + 4);
    graphics.closePath();
    graphics.fillPath();

    // Outline
    graphics.lineStyle(1, 0x008800, 1);
    graphics.beginPath();
    graphics.moveTo(halfSize, halfSize - 5);
    graphics.lineTo(halfSize + 5, halfSize);
    graphics.lineTo(halfSize, halfSize + 5);
    graphics.lineTo(halfSize - 5, halfSize);
    graphics.closePath();
    graphics.strokePath();

    // Bright shine spots
    graphics.fillStyle(0xFFFFFF, 0.9);
    graphics.fillCircle(halfSize - 1.5, halfSize - 2, 1.5);
    graphics.fillStyle(0xCCFFCC, 0.7);
    graphics.fillCircle(halfSize + 1, halfSize - 1, 1);

    graphics.generateTexture('xp-gem', size, size);
    graphics.destroy();
  }

  public setMagnetRange(range: number): void {
    this.magnetRange = range;
  }

  update(): void {
    if (!this.active || !this.player) return;

    // Calculate distance to player
    const distance = Phaser.Math.Distance.Between(
      this.x,
      this.y,
      this.player.x,
      this.player.y
    );

    // Magnet effect - pull towards player if close enough
    if (distance < this.magnetRange) {
      const direction = new Phaser.Math.Vector2(
        this.player.x - this.x,
        this.player.y - this.y
      );

      direction.normalize();
      direction.scale(this.magnetSpeed);

      this.setVelocity(direction.x, direction.y);
    } else {
      this.setVelocity(0, 0);
    }

    // Collect if touching player
    if (distance < 20) {
      this.collect();
    }
  }

  private collect(): void {
    // Give XP to player
    this.player.addExperience(this.xpValue);

    // Emit event for sound
    this.scene.events.emit('xp-collected');

    // Destroy gem (skip particles for performance)
    this.destroy();
  }
}
